import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue } from "@multiweb/types/contracts";
import { IHTMLElementWithControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { PromiseUtils } from "../../../../../../../Global/Base/Promises/PromiseUtils";
import { AttrValueReader } from "../../../../../../../OW.Remoting/Remoting/AttrValueReader";
import { IOWObject } from "../../../../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "../../../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IValidatable } from "../../../../../../UI.BaseControls/ControlInterfaces";
import { GenericControl } from "../../../../../../UI.BaseControls/GenericControl";
import { Column } from "../../../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ILovColumnParameters } from "../../../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { EditItemTemplate } from "../../../../../../UI.Controls/DataGridSubcontrols/Templates/EditItemTemplate";
import { IEditItemTemplateMasterRef } from "../../../../../../UI.Controls/DataGridSubcontrols/Templates/ITemplate";
import { ElementUtils } from "../../../../../../UI.ElementarControls/Helpers/ElementUtils";
import { LOVEdit } from "../../../Controls/LOVEdit";
import { IForm } from "../../../IForm";
import { IMasterObjectRequestDetail, LovColumn } from "../LOVColumn";

const logger = LazyLoadingSectionLogger.create(LoggingSection.lOVEdit);

export class LOVEditTemplate extends EditItemTemplate implements IEditItemTemplateMasterRef {
    public override updateData(dataSet: IAttrValue[], item: HTMLElement): void {
        const data = AttrValueReader.read(this._getDataFromSet(dataSet));
        if (!(data instanceof IOWObject))
            return;

        const bc = item as LOVEdit;
        this._updateValue(bc, data);
    }

    private _updateValue(lovEdit: LOVEdit, value: any): void {
        // musi se odregistrovat listener a zmenit hodnota, aby nedoslo k znovuvyvloani udalosti change
        const changeListeners: Array<(e: Event) => void> = lovEdit.getEventListeners("change");
        lovEdit.unregisterEventListener("change");
        lovEdit.value = value;
        for (const l of changeListeners) {
            lovEdit.registerEventListener("change", l);
        }
    }

    protected override _createEditElement(column: Column, dataSet: IAttrValue[], parentItem: HTMLElement): { element: HTMLElement; validatableControl: IValidatable } {
        const editableControl = new LOVEdit();
        const lovColumn = this.parentColumn as LovColumn;
        let defaultWhisperer: string | null = undefined;
        if (lovColumn.additionalParameters && lovColumn.additionalParameters instanceof ILovColumnParameters) {
            editableControl.readVersioned = lovColumn.additionalParameters.readVersioned;
            defaultWhisperer = lovColumn.additionalParameters.defaultWhisperer;
        }
        editableControl.attrName = this.parentColumn.attrName;
        editableControl.setInnerAttribute("data-showattrs", lovColumn?.showAttrs.join(";"));
        editableControl.setInnerAttribute("data-attrname", this.parentColumn.attrName);
        editableControl.setInnerAttribute("data-editwindow", lovColumn.editWindow);
        editableControl.setInnerAttribute("data-orderby", lovColumn.orderby);
        editableControl.setInnerAttribute("data-showonlydetail", String(lovColumn.showOnlyDetail));
        if (lovColumn.lovStyle)
            editableControl.setInnerAttribute("data-lovstyle", lovColumn.lovStyle);
        if (lovColumn.condition)
            editableControl.setInnerAttribute("data-Condition", lovColumn.condition);
        if (lovColumn.conditionParams)
            editableControl.setInnerAttribute("data-ConditionParams", lovColumn.conditionParams);
        if (lovColumn.validationClassName)
            editableControl.setInnerAttribute("data-validationclassname", lovColumn.validationClassName);

        editableControl.renderLov(defaultWhisperer);
        editableControl.lovEdit.className = lovColumn.ClassName;
        editableControl.lovEdit.enabledVisual = false; //předejde se tak možnosti editovat hodnotu dříve než to povolí práva

        // zobrazení hodnoty po nastavení aktuálního objektu
        editableControl.waitForActualObjectSet().then(async () => {
            if (editableControl.actualObject) {
                this._updateValue(editableControl, await editableControl.actualObject.getValue(editableControl.attrName));
            }
        });

        // v dalším ticku, až bude element v DOMu, aby šlo hledat rodiče
        PromiseUtils.delay(0).then(() => {
            editableControl.lovEdit.ownerForm = (GenericControl.findParent(editableControl as HTMLElement, (el) => el.classList.contains("Form")) as IHTMLElementWithControl)?.tscControl as IForm;
            const id = GenericControl.findParent(editableControl as HTMLElement, (el) => el.classList.contains("DataRow")).getAttribute("datakey");
            ElementUtils.callEvent(column as HTMLElement, "masterObjectRequest", {
                control: editableControl,
                objectId: id != null ? id : "",
                data: AttrValueReader.read(this._getDataFromSet(dataSet)),
            } as IMasterObjectRequestDetail, true, true);
        });

        // listenery je potřeba vždy po přidání do DOMu znovu zaregistrovat
        editableControl.afterAttach(() => {
            editableControl.registerEventListener("change", (e) => {
                e.stopPropagation();
                logger.log(`Element '${(e.target as Element).tagName}' is changed`);
                ElementUtils.callEvent(parentItem, "valuechanged", { value: editableControl.value, column: this.parentColumn });
            });
        });
        return { element: editableControl as HTMLElement, validatableControl: editableControl };
    }

    /**
     * Aktualizuje instanci objektu
     * @param obj nový objekt
     * @param item HTML element reprentující buňku sloupce
     */
    public setInstance(obj: OWObject, item: HTMLElement): void {
        const currentTextLOVEdit = item as LOVEdit;
        currentTextLOVEdit.actualObject = obj;
    }
}
